/**
 * Coming Soon Landing Page
 *
 * Pre-launch waitlist signup page for OutdoorGuides platform.
 * Implements MVP 0.1 specification with:
 * - Mobile-first responsive design
 * - Enhanced card-based role selection
 * - Strategic market focus (Colorado & Wyoming)
 * - Trust signals and conversion optimization
 * - Brand-consistent emerald color scheme
 * - Google Analytics 4 event tracking
 * - Rate limiting and CAPTCHA protection
 * - WCAG 2.1 AA accessibility compliance
 *
 * @version 2.0
 * @created 2025-11-19
 * @epic MVP-0.1
 */

import React, { useState, useEffect } from 'react';
import { useLoaderData, useLocation } from 'react-router-dom';
import useConsentPreference from '../../hooks/useConsentPreference';
import { useOrchestration } from '../../hooks/useOrchestration';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import SignupRequestView from '../../components/llac/SignupRequestView';
import EventRSVPView from '../../components/llac/EventRSVPView';
import EventCheckinView from '../../components/llac/EventCheckinView';
import EventEndedView from '../../components/llac/EventEndedView';
import {
  Mountain as _Mountain,
  DollarSign,
  ShieldCheck,
  CheckCircle,
  Shield,
  Mail as _Mail,
  Facebook,
  Instagram,
  Twitter,
  Linkedin,
  User
} from 'lucide-react';
import Logo from '../../components/common/Logo';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3030';

const ComingSoonPage = () => {
  const { user } = useLoaderData();

  // Check for confirmation success from URL params
  const [searchParams] = React.useState(() => {
    if (typeof window !== 'undefined') {
      return new URLSearchParams(window.location.search);
    }
    return new URLSearchParams();
  });
  const confirmed = searchParams.get('confirmed'); // 'true' | 'already' | null

  // Form state
  const [formData, setFormData] = useState({
    email: '',
    role: '' // 'guide' | 'customer' | ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(confirmed ? 'confirmed' : 'idle'); // 'idle' | 'success' | 'error' | 'confirmed' | 'already-on-list' | 'already-converted'
  const [errorMessage, setErrorMessage] = useState('');

  // SCRUM-1450: Orchestration directive — render correct view based on ?c= intent code
  const { directive, loading: directiveLoading, intentCode } = useOrchestration();

  // SCRUM-1448: Consent-gated analytics
  const { consent } = useConsentPreference();

  // Analytics Helper: Track events to Google Analytics 4
  const trackEvent = (eventName, parameters = {}) => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', eventName, parameters);
    }
  };

  // Extract UTM parameters — sessionStorage first (AC-04), fallback to URL.
  // All sessionStorage access wrapped in try/catch for private-mode safety (AC-07).
  const getUTMParameters = () => {
    if (typeof window === 'undefined') {return {};}

    // AC-04: Read from sessionStorage first (written on consent grant)
    try {
      const stored = sessionStorage.getItem('odg_utm');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed && typeof parsed === 'object') {
          return parsed;
        }
      }
    } catch (_e) {
      // Private mode or parse failure — fall through to URL
    }

    // Fallback: read UTMs from current URL params
    const params = new URLSearchParams(window.location.search);
    const utmData = {};

    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach(param => {
      const value = params.get(param);
      if (value) {
        // Remove 'utm_' prefix for API
        const key = param.replace('utm_', '');
        utmData[key === 'source' ? 'utmSource' : key === 'medium' ? 'utmMedium' : key === 'campaign' ? 'utmCampaign' : key === 'content' ? 'utmContent' : 'utmTerm'] = value;
      }
    });

    return utmData;
  };

  // SCRUM-1448: Gate GA4 initialisation on analytics consent.
  // Fires on mount if already consented, or when consent changes mid-session.
  // Individual gtag event calls are not guarded — uninitialised gtag silently drops them.
  useEffect(() => {
    if (consent?.analytics === true && typeof window.gtag === 'function') {
      window.gtag('config', 'G-REPWMGV5Y5');
    }
  }, [consent?.analytics]);

  // SCRUM-1448/1460: Gate UTM sessionStorage write on analytics consent.
  // Captures UTMs from current URL when consent is granted (including mid-session).
  // Reads from URL directly here (not via getUTMParameters) to avoid sessionStorage
  // read-before-write on first consent grant. Wrapped in try/catch for private mode (AC-07).
  useEffect(() => {
    if (consent?.analytics === true) {
      const params = new URLSearchParams(window.location.search);
      const utmData = {};
      ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach(param => {
        const value = params.get(param);
        if (value) {
          const key = param.replace('utm_', '');
          utmData[key === 'source' ? 'utmSource' : key === 'medium' ? 'utmMedium' : key === 'campaign' ? 'utmCampaign' : key === 'content' ? 'utmContent' : 'utmTerm'] = value;
        }
      });
      if (Object.keys(utmData).length > 0) {
        try {
          // Only write if not already set — preserve first-touch UTMs (AC-01)
          if (!sessionStorage.getItem('odg_utm')) {
            sessionStorage.setItem('odg_utm', JSON.stringify(utmData));
          }
        } catch (_e) {
          // Private mode — write silently fails, UTMs read from URL at submit time
        }
      }
    }
  }, [consent?.analytics]);

  const { hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) { el.scrollIntoView({ behavior: 'smooth' }); }
    }
  }, [hash]);

  // CDN URL for static assets
  const CDN_URL = import.meta.env.VITE_CDN_URL || '';

  // Target markets data
  const targetMarkets = [
    {
      id: 'colorado',
      name: 'Colorado Front Range',
      tagline: 'Gateway to the Rockies',
      image: `${CDN_URL}/images/markets/colorado.jpg`,
      gradient: 'from-emerald-600 to-emerald-800'
    },
    {
      id: 'jackson-hole',
      name: 'Jackson Hole, WY',
      tagline: 'Adventure Capital',
      image: `${CDN_URL}/images/markets/jackson-hole.jpg`,
      gradient: 'from-emerald-700 to-emerald-900'
    }
  ];

  // Form handlers
  const handleEmailChange = (_e) => {
    setFormData({ ...formData, email: _e.target.value });
    setSubmitStatus('idle');
    setErrorMessage('');

    // Track form interaction (first time only)
    if (_e.target.value && !formData.email) {
      trackEvent('form_start', { form_name: 'waitlist_signup' });
    }
  };

  const handleRoleChange = (role) => {
    setFormData({ ...formData, role });

    // Track role selection
    trackEvent('select_content', {
      content_type: 'role_selection',
      item_id: role
    });
  };

  const handleSubmit = async (_e) => {
    _e.preventDefault();

    // Basic validation
    if (!formData.email) {
      setErrorMessage('Please enter your email address');
      setSubmitStatus('error');
      trackEvent('form_error', { error_type: 'empty_email' });
      return;
    }

    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setErrorMessage('Please enter a valid email address');
      setSubmitStatus('error');
      trackEvent('form_error', { error_type: 'invalid_email' });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // Get UTM parameters and referrer
      const utmParams = getUTMParameters();
      const referrer = document.referrer || null;
      const landingPage = window.location.pathname;

      const response = await fetch(`${API_BASE_URL}/api/v1/pre-launch/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          interestedRole: formData.role || null,
          ...utmParams,
          landingPage,
          referrer
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        if (data.alreadyConverted) {
          setSubmitStatus('already-converted');
          setFormData({ email: '', role: '' });
          return;
        }

        if (data.alreadyConfirmed) {
          setSubmitStatus('already-on-list');
          setFormData({ email: '', role: '' });
          return;
        }

        // Fresh signup or auto-resend for unconfirmed — show "Check Your Email!"
        setSubmitStatus('success');

        // Track successful conversion
        trackEvent('generate_lead', {
          role: formData.role || 'unknown',
          source: utmParams.utmSource || 'direct',
          value: 1
        });

        // SCRUM-1448/1460: Clear UTM sessionStorage after successful form submit (AC-05)
        try {
          sessionStorage.removeItem('odg_utm');
        } catch (_e) {
          // Private mode — no-op
        }

        // Reset form
        setFormData({ email: '', role: '' });
      } else {
        setSubmitStatus('error');

        // Handle specific error cases
        if (response.status === 429) {
          setErrorMessage('Too many requests. Please try again in an hour.');
          trackEvent('form_error', { error_type: 'rate_limited' });
        } else if (data.error) {
          setErrorMessage(data.error);
          trackEvent('form_error', {
            error_type: data.error.includes('already') ? 'duplicate_email' : 'api_error'
          });
        } else {
          setErrorMessage('Something went wrong. Please try again.');
          trackEvent('form_error', { error_type: 'unknown' });
        }
      }
    } catch (error) {
      setSubmitStatus('error');
      setErrorMessage('Network error. Please check your connection and try again.');
      trackEvent('form_error', { error_type: 'network_error' });
      console.error('Waitlist signup error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // SCRUM-1450: Directive-based view dispatch
  // AC-03: While loading, show existing lander (no blank flash)
  // AC-04: No directive (no code or default_lander) → existing page unchanged
  if (directive && !directiveLoading) {
    const { view, event, market } = directive;

    if (view === 'rsvp' || view === 'event_rsvp') {
      return (
        <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
          <Header />
          <main style={{ flex: 1 }}>
            <EventRSVPView event={event} intentCode={intentCode} marketSlug={market?.slug} />
          </main>
          <Footer />
        </div>
      );
    }
    if (view === 'checkin') {
      return (
        <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
          <Header />
          <main style={{ flex: 1 }}>
            <EventCheckinView event={event} intentCode={intentCode} marketSlug={market?.slug} />
          </main>
          <Footer />
        </div>
      );
    }
    if (view === 'signup_request') {
      return (
        <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
          <Header />
          <main style={{ flex: 1 }}>
            <SignupRequestView marketSlug={market?.slug || ''} intentCode={intentCode} utm={directive?.utm || null} />
          </main>
          <Footer />
        </div>
      );
    }
    // AC-09, AC-10: invalid_code / event_not_found → fall through to default lander silently
    // AC-08: event_ended renders message ABOVE the default lander
  }

  return (
    <div className="min-h-screen bg-white">
      {/* SCRUM-1450: Event ended banner above default lander (AC-08) */}
      {directive && !directiveLoading && directive.view === 'event_ended' && (
        <EventEndedView event={directive.event} />
      )}

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Logo />

            <div className="flex items-center gap-4">
              <a
                href="#signup"
                className="hidden sm:inline-flex px-4 py-2 text-sm font-medium text-emerald-600 hover:text-emerald-700 transition-colors"
              >
                Join Waitlist
              </a>

              {/* Account icon - only shown when logged in */}
              {user && (
                <a
                  href={(user.roles?.includes('guide') || user.primaryRole === 'guide') ? '/guides/dashboard' : '/customer/dashboard'}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 hover:text-emerald-600 transition-colors rounded-lg hover:bg-gray-100"
                  aria-label="Go to dashboard"
                >
                  <User className="w-5 h-5" />
                  <span className="hidden md:inline">Account</span>
                </a>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center pt-20">
        {/* Background with Gradient Overlay */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-900 via-emerald-800 to-gray-900">
            {/* Decorative SVG pattern */}
            <div className="absolute inset-0 opacity-20">
              <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                <polygon points="0,100 50,0 100,100" fill="currentColor" className="text-emerald-700" />
              </svg>
            </div>
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-gray-900/60 to-gray-900/40" />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-6">
            Your Next Adventure
            <br />
            Starts Here
          </h1>

          <p className="text-lg sm:text-xl md:text-2xl text-white/90 mb-2 max-w-2xl mx-auto">
            Connect with professional outdoor guides in the heart of the Rockies.
          </p>

          <p className="text-xl sm:text-2xl text-emerald-400 font-semibold mb-12">
            Starting in Colorado & Wyoming
          </p>

          {/* Email Capture Form */}
          <div id="signup" className="w-full max-w-xl mx-auto">
            {submitStatus === 'confirmed' ? (
              <div className="bg-white rounded-2xl shadow-2xl p-8 text-center">
                <CheckCircle className="w-16 h-16 text-emerald-600 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {confirmed === 'already' ? "You&apos;re Already on the List!" : "Email Confirmed!"}
                </h3>
                <p className="text-gray-600 mb-6">
                  {confirmed === 'already'
                    ? "Your email is already confirmed. You're on our waitlist and we'll keep you informed about our launch."
                    : "Thank you for confirming your email! You're now on our waitlist and we'll keep you informed about our launch."}
                </p>

                {/* What's Next */}
                <div className="bg-emerald-50 rounded-xl p-6 mb-6 text-left">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 text-center">
                    What&apos;s Next?
                  </h4>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-emerald-600 mr-3 mt-0.5 flex-shrink-0" />
                      <span>We&apos;ll send you exclusive updates as we build</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-emerald-600 mr-3 mt-0.5 flex-shrink-0" />
                      <span>You&apos;ll get early access when we launch</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-emerald-600 mr-3 mt-0.5 flex-shrink-0" />
                      <span>Be the first to know about launch date</span>
                    </li>
                  </ul>
                </div>

                <button
                  onClick={() => { setSubmitStatus('idle'); window.history.replaceState({}, '', '/coming-soon'); }}
                  className="text-emerald-600 hover:text-emerald-700 font-medium"
                >
                  Sign up another email
                </button>
              </div>
            ) : submitStatus === 'already-on-list' ? (
              <div className="bg-white rounded-2xl shadow-2xl p-8 text-center">
                <CheckCircle className="w-16 h-16 text-emerald-600 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  You&apos;re Already on the List!
                </h3>
                <p className="text-gray-600 mb-6">
                  Great news! You&apos;re already on our waitlist and we&apos;ll keep you informed about our launch.
                </p>

                {/* What's Next */}
                <div className="bg-emerald-50 rounded-xl p-6 mb-6 text-left">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 text-center">
                    What&apos;s Next?
                  </h4>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-emerald-600 mr-3 mt-0.5 flex-shrink-0" />
                      <span>We&apos;ll send you exclusive updates as we build</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-emerald-600 mr-3 mt-0.5 flex-shrink-0" />
                      <span>You&apos;ll get early access when we launch</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-emerald-600 mr-3 mt-0.5 flex-shrink-0" />
                      <span>Be the first to know about launch date</span>
                    </li>
                  </ul>
                </div>

                <button
                  onClick={() => setSubmitStatus('idle')}
                  className="text-emerald-600 hover:text-emerald-700 font-medium"
                >
                  Sign up another email
                </button>
              </div>
            ) : submitStatus === 'already-converted' ? (
              <div className="bg-white rounded-2xl shadow-2xl p-8 text-center">
                <CheckCircle className="w-16 h-16 text-emerald-600 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  You Already Have an Account
                </h3>
                <p className="text-gray-600 mb-6">
                  It looks like you&apos;ve already created an account with us. Log in to access your dashboard.
                </p>
                <a
                  href="/login"
                  className="inline-flex px-8 py-3 bg-emerald-600 text-white font-semibold rounded-full hover:bg-emerald-700 transition-colors mb-4"
                >
                  Log In
                </a>
                <div>
                  <button
                    onClick={() => setSubmitStatus('idle')}
                    className="text-emerald-600 hover:text-emerald-700 font-medium"
                  >
                    Sign up another email
                  </button>
                </div>
              </div>
            ) : submitStatus === 'success' ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-8 text-center">
                <CheckCircle className="w-16 h-16 text-emerald-600 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Check Your Email!
                </h3>
                <p className="text-gray-600 mb-6">
                  We&apos;ve sent you a confirmation email. Please click the link in the email to confirm your address and join the waitlist.
                </p>
                <button
                  onClick={() => setSubmitStatus('idle')}
                  className="text-emerald-600 hover:text-emerald-700 font-medium"
                >
                  Sign up another email
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Email Input */}
                <div className="flex flex-col sm:flex-row gap-3 bg-white/10 backdrop-blur-sm p-2 rounded-full">
                  <input
                    type="email"
                    value={formData.email}
                    onChange={handleEmailChange}
                    placeholder="Enter your email"
                    className="flex-1 px-6 py-3 rounded-full bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-base"
                    disabled={isSubmitting}
                    autoComplete="email"
                    aria-label="Email address"
                    aria-required="true"
                    aria-invalid={submitStatus === 'error'}
                    aria-describedby={submitStatus === 'error' ? 'email-error' : undefined}
                  />
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-8 py-3 bg-emerald-600 text-white font-semibold rounded-full hover:bg-emerald-700 transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                    aria-label={isSubmitting ? 'Submitting...' : 'Join waitlist'}
                    aria-busy={isSubmitting}
                  >
                    {isSubmitting ? (
                      <span className="flex items-center gap-2">
                        <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Joining...
                      </span>
                    ) : (
                      'Join Waitlist'
                    )}
                  </button>
                </div>

                {/* Trust Signals */}
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 text-white/80 text-sm">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 flex-shrink-0" />
                    <span>Early access priority</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 flex-shrink-0" />
                    <span>No spam, ever</span>
                  </div>
                </div>

                {/* Error Message */}
                {submitStatus === 'error' && errorMessage && (
                  <div
                    id="email-error"
                    role="alert"
                    aria-live="polite"
                    className="text-red-400 text-sm bg-red-900/20 backdrop-blur-sm px-4 py-2 rounded-lg"
                  >
                    {errorMessage}
                  </div>
                )}

                {/* Role Selection */}
                <div className="pt-4">
                  <div className="space-y-3">
                    <label className="block text-white/90 text-sm font-medium text-center">
                      I am a: <span className="text-white/60">(optional)</span>
                    </label>

                    {/* Guide Option */}
                    <label className="block cursor-pointer group">
                      <input
                        type="radio"
                        name="role"
                        value="guide"
                        checked={formData.role === 'guide'}
                        onChange={() => handleRoleChange('guide')}
                        className="sr-only peer"
                        aria-label="I'm a Professional Guide - Keep 100% of booking fees"
                      />
                      <div className="bg-white/10 backdrop-blur-sm border-2 border-white/20 rounded-2xl p-4 transition-all duration-200 group-hover:bg-white/15 group-hover:border-white/30 peer-checked:bg-emerald-600/20 peer-checked:border-emerald-400 peer-checked:shadow-lg peer-checked:shadow-emerald-500/20">
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-5 h-5 rounded-full border-2 border-white/40 flex items-center justify-center mt-0.5 peer-checked:border-emerald-400 peer-checked:bg-emerald-400">
                            <div className={`w-2 h-2 rounded-full bg-white transition-opacity ${formData.role === 'guide' ? 'opacity-100' : 'opacity-0'}`}></div>
                          </div>
                          <div className="flex-1">
                            <div className="text-white font-semibold text-base mb-1">Professional Guide</div>
                            <div className="text-white/80 text-sm">Keep 100% of your booking fees</div>
                          </div>
                        </div>
                      </div>
                    </label>

                    {/* Customer Option */}
                    <label className="block cursor-pointer group">
                      <input
                        type="radio"
                        name="role"
                        value="customer"
                        checked={formData.role === 'customer'}
                        onChange={() => handleRoleChange('customer')}
                        className="sr-only peer"
                        aria-label="I'm a Customer - Book verified professional guides"
                      />
                      <div className="bg-white/10 backdrop-blur-sm border-2 border-white/20 rounded-2xl p-4 transition-all duration-200 group-hover:bg-white/15 group-hover:border-white/30 peer-checked:bg-emerald-600/20 peer-checked:border-emerald-400 peer-checked:shadow-lg peer-checked:shadow-emerald-500/20">
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-5 h-5 rounded-full border-2 border-white/40 flex items-center justify-center mt-0.5 peer-checked:border-emerald-400 peer-checked:bg-emerald-400">
                            <div className={`w-2 h-2 rounded-full bg-white transition-opacity ${formData.role === 'customer' ? 'opacity-100' : 'opacity-0'}`}></div>
                          </div>
                          <div className="flex-1">
                            <div className="text-white font-semibold text-base mb-1">Adventure Seeker</div>
                            <div className="text-white/80 text-sm">Book authentic outdoor experiences</div>
                          </div>
                        </div>
                      </div>
                    </label>
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Value Proposition Section */}
      <section id="features" className="py-16 sm:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-12 sm:mb-16 text-gray-900">
            Built for Professional Guides
          </h2>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-12">
            {/* Value Prop 1 */}
            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <DollarSign className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-xl font-semibold mb-4 text-gray-900">
                Keep 100% of Your Booking Fees
              </h3>
              <p className="text-gray-600">
                Customers pay a small 8% fee. You keep every dollar you earn. No platform commissions.
              </p>
            </div>

            {/* Value Prop 2 */}
            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <ShieldCheck className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-xl font-semibold mb-4 text-gray-900">
                Verified Expert Guides
              </h3>
              <p className="text-gray-600">
                Rigorous verification ensures quality experiences for every customer. Professional credentials required.
              </p>
            </div>

            {/* Value Prop 3 */}
            <div className="text-center sm:col-span-2 lg:col-span-1">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <_Mountain className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-xl font-semibold mb-4 text-gray-900">
                Starting in the Rockies
              </h3>
              <p className="text-gray-600">
                Launching first in Colorado & Wyoming, with expansion to more destinations coming soon.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-12 sm:mb-16 text-gray-900">
            How It Works
          </h2>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Step 1 */}
            <div className="relative">
              <div className="text-center">
                <div className="w-12 h-12 bg-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4 text-white font-bold text-xl">
                  1
                </div>
                <h3 className="text-lg font-semibold mb-2 text-gray-900">Sign Up</h3>
                <p className="text-gray-600 text-sm">Create your profile in minutes</p>
              </div>
              <div className="hidden lg:block absolute top-6 left-[60%] w-[80%] h-0.5 bg-emerald-200" />
            </div>

            {/* Step 2 */}
            <div className="relative">
              <div className="text-center">
                <div className="w-12 h-12 bg-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4 text-white font-bold text-xl">
                  2
                </div>
                <h3 className="text-lg font-semibold mb-2 text-gray-900">Get Verified</h3>
                <p className="text-gray-600 text-sm">We validate your credentials and experience</p>
              </div>
              <div className="hidden lg:block absolute top-6 left-[60%] w-[80%] h-0.5 bg-emerald-200" />
            </div>

            {/* Step 3 */}
            <div className="relative">
              <div className="text-center">
                <div className="w-12 h-12 bg-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4 text-white font-bold text-xl">
                  3
                </div>
                <h3 className="text-lg font-semibold mb-2 text-gray-900">List Services</h3>
                <p className="text-gray-600 text-sm">Set your pricing and availability</p>
              </div>
              <div className="hidden lg:block absolute top-6 left-[60%] w-[80%] h-0.5 bg-emerald-200" />
            </div>

            {/* Step 4 */}
            <div className="text-center">
              <div className="w-12 h-12 bg-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4 text-white font-bold text-xl">
                4
              </div>
              <h3 className="text-lg font-semibold mb-2 text-gray-900">Start Guiding</h3>
              <p className="text-gray-600 text-sm">Customers book directly, you keep 100%</p>
            </div>
          </div>
        </div>
      </section>

      {/* Target Markets Section */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-12 sm:mb-16 text-gray-900">
            Starting in Colorado & Wyoming
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {targetMarkets.map((market) => (
              <div
                key={market.id}
                className="relative overflow-hidden rounded-2xl shadow-lg group cursor-pointer aspect-[4/3]"
              >
                {/* Background image */}
                <img
                  src={market.image}
                  alt={market.name}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />

                {/* Gradient overlay for text legibility */}
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 to-transparent" />

                <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6">
                  <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-white">
                    {market.name}
                  </h3>
                  <p className="text-white/80 text-xs sm:text-sm mt-1">
                    {market.tagline}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-gray-900">
            Join the Community
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 mb-8">
            Be among the first members to get exclusive early access and founding member benefits.
          </p>
          <a
            href="#signup"
            className="inline-flex items-center gap-2 px-8 py-3 bg-emerald-600 text-white font-semibold rounded-full hover:bg-emerald-700 transition-colors"
          >
            <_Mail className="w-5 h-5" />
            Join the Waitlist
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-50 border-t border-gray-200 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center space-y-8">
            {/* Logo */}
            <Logo />

            {/* Tagline */}
            <p className="text-gray-600 text-center max-w-md">
              Connecting professional outdoor guides with adventure seekers in Colorado & Wyoming.
            </p>

            {/* Social Media */}
            <div className="hidden flex items-center gap-6">
              <a
                href="#"
                className="text-gray-400 hover:text-emerald-600 transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="w-6 h-6" />
              </a>
              <a
                href="#"
                className="text-gray-400 hover:text-emerald-600 transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="w-6 h-6" />
              </a>
              <a
                href="#"
                className="text-gray-400 hover:text-emerald-600 transition-colors"
                aria-label="Twitter"
              >
                <Twitter className="w-6 h-6" />
              </a>
              <a
                href="#"
                className="text-gray-400 hover:text-emerald-600 transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-6 h-6" />
              </a>
            </div>

            {/* Links */}
            <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-600">
              <a href="/privacy" className="hover:text-emerald-600 transition-colors">
                Privacy Policy
              </a>
              <a href="/terms" className="hover:text-emerald-600 transition-colors">
                Terms of Service
              </a>
              <a href="mailto:help@outdoorguides.com" className="hover:text-emerald-600 transition-colors">
                Contact Us
              </a>
            </div>

            {/* Copyright */}
            <p className="text-sm text-gray-500">
              © {new Date().getFullYear()} OutdoorGuides. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ComingSoonPage;
